import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { blogPosts } from '../utils/blogData';
import SEO from '../components/SEO';
import VideoBackground from '../components/VideoBackground';

const BlogCard = ({ post }) => (
  <Link
    to={`/blogs/${post.slug}`}
    className="group block h-full"
    data-testid={`blog-card-${post.slug}`}
  >
    <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden h-full">
      <div className="overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <span
            className="text-xs font-semibold px-3 py-1 rounded-full"
            style={{ backgroundColor: '#AABC5A20', color: '#587740' }}
          >
            {post.category}
          </span>
          <span className="flex items-center text-xs" style={{ color: '#999' }}>
            <Calendar className="w-3 h-3 mr-1" />
            {post.date}
          </span>
        </div>
        <h2
          className="text-lg font-bold mb-3 group-hover:underline decoration-2 underline-offset-4"
          style={{ color: '#587740' }}
        >
          {post.title}
        </h2>
        <p
          className="text-sm leading-relaxed mb-4"
          style={{ color: '#5e5e5e' }}
        >
          {post.excerpt}
        </p>
        <span
          className="inline-flex items-center text-sm font-semibold"
          style={{ color: '#81b956' }}
        >
          Read More
          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </span>
      </CardContent>
    </Card>
  </Link>
);

const BlogsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO title="Blog" description="Stay informed with the latest insights, guides and news about NDIS support services, disability care, and community participation from Life Care and Wellness." />
      <Header />

      {/* Hero */}
      <VideoBackground variant="light" className="pt-32 pb-16">
        <div className="container mx-auto px-4 text-center">
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
            style={{ color: '#587740' }}
            data-testid="blogs-page-title"
          >
            Our Blog
          </h1>
          <p
            className="text-lg md:text-xl max-w-3xl mx-auto"
            style={{ color: '#5e5e5e' }}
          >
            Stay informed with the latest insights, guides, and news about NDIS
            support services, disability care, and community participation.
          </p>
        </div>
      </VideoBackground>

      {/* Blog Carousel */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto px-14" data-testid="blog-carousel">
            <Carousel
              opts={{
                align: 'start',
                loop: true,
              }}
            >
              <CarouselContent>
                {blogPosts.map((post) => (
                  <CarouselItem
                    key={post.id}
                    className="basis-full md:basis-1/2 lg:basis-1/3"
                  >
                    <div className="p-2 h-full">
                      <BlogCard post={post} />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious
                className="h-10 w-10 border-2"
                style={{ borderColor: '#81b956', color: '#81b956' }}
                data-testid="blog-carousel-prev"
              />
              <CarouselNext
                className="h-10 w-10 border-2"
                style={{ borderColor: '#81b956', color: '#81b956' }}
                data-testid="blog-carousel-next"
              />
            </Carousel>
          </div>
        </div>
      </section>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default BlogsPage;
